<template>
  <div class="overflow-hidden rounded border border-sushi bg-white shadow-sm">
    <div class="flex min-h-[68px]">
      <div class="flex w-24 shrink-0 flex-col items-center justify-center bg-gradient-to-b from-cerulean to-keppel px-2 py-2 text-center text-white">
        <div class="text-xl font-semibold leading-none tabular-nums">
          {{ formattedTime }}
        </div>
        <div class="mt-1 text-[0.62rem] font-semibold leading-tight text-white/90">
          TIME<br>REMAINING
        </div>
      </div>

      <div class="flex w-9 shrink-0 items-center justify-center bg-keppel text-white/90">
        <span class="grid grid-cols-3 gap-0.5" aria-hidden="true">
          <span
            v-for="dot in 9"
            :key="dot"
            class="h-1 w-1 rounded-full bg-current"
          />
        </span>
      </div>

      <div class="min-w-0 flex-1 px-3 py-2">
        <div class="flex min-w-0 items-start justify-between gap-4">
          <div class="min-w-0">
            <h3 class="truncate text-base font-semibold leading-tight text-gray-900">
              <a
                :href="`https://perbyte.accelo.com/?action=view_task&id=${props.task.id}`"
                target="_blank"
                class="hover:underline"
              >
                {{ props.task.title }}
              </a>
            </h3>
            <p class="mt-0.5 truncate text-sm text-gray-600">
              {{ contextPrefix }}
              <a
                v-if="againstUrl"
                :href="againstUrl"
                target="_blank"
                class="font-medium text-keppel hover:underline"
              >
                {{ againstLabel }}
              </a>
              <span v-else>{{ againstLabel }}</span>
              <span v-if="contextSuffix">{{ contextSuffix }}</span>
            </p>
            <p
              v-if="companyName"
              class="mt-0.5 truncate text-xs font-medium text-keppel"
            >
              {{ companyName }}
            </p>
          </div>

          <div class="flex shrink-0 items-center gap-2 text-right">
            <span
              v-if="isAtRisk"
              class="rounded bg-red-50 px-2 py-1 text-xs font-semibold text-red-700"
              :title="atRiskReason"
            >
              Attention
            </span>
            <span class="text-sm font-semibold text-gray-600">
              {{ dateLabel }}
            </span>
            <button
              type="button"
              class="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
              :aria-expanded="isExpanded"
              :aria-label="isExpanded ? 'Collapse task details' : 'Expand task details'"
              @click="isExpanded = !isExpanded"
            >
              <ChevronUp v-if="isExpanded" class="h-4 w-4" />
              <ChevronDown v-else class="h-4 w-4" />
            </button>
          </div>
        </div>

        <div class="task-progress group relative mt-2 cursor-pointer">
          <div class="task-progress__track">
            <div
              class="task-progress__logged"
              :style="{ width: `${loggedPct}%` }"
            />
            <div
              class="task-progress__remaining"
              :style="{ width: `${remainingPct}%` }"
            />
            <div
              v-if="loggedPct + remainingPct < 100"
              class="task-progress__empty"
            />
          </div>

          <div
            class="absolute -top-9 left-1/2 transform -translate-x-1/2 hidden group-hover:flex z-10 shadow rounded overflow-hidden text-white text-xs"
          >
            <div class="bg-sushi px-2 py-1">
              {{ loggedFormatted }} work done
            </div>
            <div class="bg-cerulean px-2 py-1">
              {{ remainingFormatted }} remaining
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isExpanded"
      class="border-t border-gray-200 bg-gray-50 px-4 py-4"
    >
      <div class="task-detail-grid">
        <aside class="space-y-5 text-sm">
          <section>
            <h4 class="task-detail-heading">
              People & Priority
            </h4>
            <dl class="mt-3 space-y-2">
              <div class="task-detail-row">
                <dt>Status</dt>
                <dd>{{ props.task.standing || props.task.status || 'Unknown' }}</dd>
              </div>
              <div class="task-detail-row">
                <dt>Priority</dt>
                <dd>{{ props.task.priority || props.task.activityPriority || 'Normal' }}</dd>
              </div>
            </dl>
          </section>

          <section>
            <h4 class="task-detail-heading">
              Dates
            </h4>
            <dl class="mt-3 space-y-2">
              <div class="task-detail-row">
                <dt>Created</dt>
                <dd>{{ formatTaskDate(props.task.dateCreated) }}</dd>
              </div>
              <div class="task-detail-row">
                <dt>Due</dt>
                <dd>{{ formatTaskDate(props.task.dateDue) }}</dd>
              </div>
              <div class="task-detail-row">
                <dt>Completed</dt>
                <dd>{{ formatTaskDate(props.task.dateCompleted) }}</dd>
              </div>
            </dl>
          </section>

          <section>
            <h4 class="task-detail-heading">
              Work
            </h4>
            <dl class="mt-3 space-y-2">
              <div class="task-detail-row">
                <dt>Budget/Estimated</dt>
                <dd>{{ budgetFormatted }}</dd>
              </div>
              <div class="task-detail-row">
                <dt>Logged</dt>
                <dd>{{ loggedFormatted }}</dd>
              </div>
              <div class="task-detail-row">
                <dt>Remaining</dt>
                <dd>{{ remainingFormatted }}</dd>
              </div>
            </dl>
            <div class="task-progress__track mt-3">
              <div
                class="task-progress__logged"
                :style="{ width: `${budgetLoggedPct}%` }"
              />
              <div class="task-progress__empty" />
            </div>
          </section>
        </aside>

        <section class="min-w-0">
          <h4 class="task-detail-heading">
            Description
          </h4>
          <div class="mt-3 rounded border border-gray-200 bg-white p-4 text-sm leading-relaxed text-gray-700">
            <p
              v-if="descriptionText"
              class="whitespace-pre-wrap"
            >
              {{ descriptionText }}
            </p>
            <p v-else class="italic text-gray-500">
              No description provided.
            </p>
          </div>

          <h4 class="task-detail-heading mt-5">
            Context
          </h4>
          <div class="mt-3 rounded border border-gray-200 bg-white p-4 text-sm text-gray-700">
            <div class="font-medium text-gray-900">
              {{ contextPrefix }}
              <a
                v-if="againstUrl"
                :href="againstUrl"
                target="_blank"
                class="text-keppel hover:underline"
              >
                {{ againstLabel }}
              </a>
              <span v-else>{{ againstLabel }}</span>
              <span v-if="contextSuffix">{{ contextSuffix }}</span>
            </div>
            <div v-if="breadcrumbs.length" class="mt-2 flex flex-wrap gap-2">
              <a
                v-for="crumb in breadcrumbs"
                :key="`${crumb.table}-${crumb.id}`"
                :href="breadcrumbUrl(crumb)"
                target="_blank"
                class="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 transition hover:bg-cerulean hover:text-white"
              >
                {{ crumb.table }}: {{ crumb.title }}
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { ChevronDown, ChevronUp } from 'lucide-vue-next'

const props = defineProps({
  task: {
    type: Object,
    required: true
  }
})

const isExpanded = ref(false)

// Format seconds to HH:MM
const formatSeconds = (sec = 0) => {
  const totalMins = Math.floor(sec / 60)
  const hours = Math.floor(totalMins / 60)
  const minutes = totalMins % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

const formattedTime = computed(() => formatSeconds(props.task.remaining))
const loggedFormatted = computed(() => formatSeconds(props.task.logged))
const remainingFormatted = computed(() => formatSeconds(props.task.remaining))
const budgetFormatted = computed(() => formatSeconds(props.task.objectBudget))

const logged = computed(() => Number(props.task.logged || 0))
const remaining = computed(() => Number(props.task.remaining || 0))
const totalTime = computed(() => logged.value + remaining.value)

const loggedPct = computed(() => totalTime.value > 0 ? (logged.value / totalTime.value) * 100 : 0)
const remainingPct = computed(() => totalTime.value > 0 ? (remaining.value / totalTime.value) * 100 : 0)
const budgetLoggedPct = computed(() => {
  const budget = Number(props.task.objectBudget || 0)
  return budget > 0 ? Math.min(100, (logged.value / budget) * 100) : 0
})

const breadcrumbs = computed(() => Array.isArray(props.task.breadcrumbs) ? props.task.breadcrumbs : [])
const companyName = computed(() => findCrumb('company')?.title || '')
const descriptionText = computed(() => {
  if (!props.task.description) return ''
  return props.task.description
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]*>/g, '')
    .trim()
})

const againstType = computed(() => (props.task.againstType || 'item').toString().toLowerCase())
const againstLabel = computed(() => {
  const against = findCrumb(againstType.value, props.task.againstId)
  return against?.title || (props.task.againstId ? `#${props.task.againstId}` : 'unknown item')
})
const againstUrl = computed(() => {
  if (!props.task.againstType || !props.task.againstId) return ''
  return acceloUrl(props.task.againstType, props.task.againstId)
})
const contextPrefix = computed(() => `${againstType.value} against `)
const contextSuffix = computed(() => {
  const project = findCrumb('job') || findCrumb('project')
  const projectSuffix = project?.title && project.title !== againstLabel.value ? ` for ${project.title}` : ''
  return companyName.value ? ` for ${companyName.value}` : projectSuffix
})

function findCrumb(table, id = null) {
  const normalizedTable = table?.toString().toLowerCase()
  return breadcrumbs.value.find((crumb) => {
    const crumbTable = crumb?.table?.toString().toLowerCase()
    if (crumbTable !== normalizedTable) return false
    return !id || crumb?.id?.toString() === id?.toString()
  })
}

function acceloUrl(type, id) {
  if (!type || !id) return ''
  const normalizedType = type.toString().toLowerCase()
  const actionByType = {
    company: 'view_company',
    issue: 'view_issue',
    ticket: 'view_issue',
    job: 'view_job',
    project: 'view_job',
    milestone: 'view_milestone',
    task: 'view_task'
  }
  const action = actionByType[normalizedType] || `view_${normalizedType}`
  return `https://perbyte.accelo.com/?action=${action}&id=${id}`
}

function breadcrumbUrl(crumb) {
  return acceloUrl(crumb?.table, crumb?.id)
}

const dateLabel = computed(() => {
  const dueDate = props.task.dateDue
  if (!dueDate) return 'No due date'

  const due = new Date(dueDate * 1000)
  const now = new Date()
  due.setHours(0, 0, 0, 0)
  now.setHours(0, 0, 0, 0)

  const diffInMs = due - now
  const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24))

  if (diffInDays === 0) return 'Due today'
  if (diffInDays > 0) return `Due in ${diffInDays} day(s)`
  return `Overdue by ${Math.abs(diffInDays)} day(s)`
})

const formatTaskDate = (epochSeconds) => {
  if (!epochSeconds) return 'Not set'
  return new Date(epochSeconds * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

// "At Risk" Logic
const isAtRisk = computed(() => {
  const now = new Date()
  const due = props.task.dateDue ? new Date(props.task.dateDue * 1000) : null
  const created = props.task.dateCreated ? new Date(props.task.dateCreated * 1000) : null

  const daysUntilDue = due ? Math.floor((due - now) / (1000 * 60 * 60 * 24)) : Infinity
  const daysSinceCreated = created ? Math.floor((now - created) / (1000 * 60 * 60 * 24)) : 0

  const budget = Number(props.task.objectBudget || 0)
  const logged = Number(props.task.logged || 0)
  const remaining = Number(props.task.remaining || 0)
  const status = props.task.standing || ''
  const isIncomplete = !props.task.dateCompleted

  return (
    (daysUntilDue <= 3 && (budget === 0 || (logged / budget) * 100 < 50)) ||
    (budget > 0 && logged > budget) ||
    (['Started', 'Blocked'].includes(status) && daysSinceCreated >= 7 && isIncomplete) ||
    (remaining === 0 && isIncomplete)
  )
})

const atRiskReason = computed(() => {
  const now = new Date()
  const due = props.task.dateDue ? new Date(props.task.dateDue * 1000) : null
  const created = props.task.dateCreated ? new Date(props.task.dateCreated * 1000) : null

  const daysUntilDue = due ? Math.floor((due - now) / (1000 * 60 * 60 * 24)) : Infinity
  const daysSinceCreated = created ? Math.floor((now - created) / (1000 * 60 * 60 * 24)) : 0

  const budget = Number(props.task.objectBudget || 0)
  const logged = Number(props.task.logged || 0)
  const remaining = Number(props.task.remaining || 0)
  const status = props.task.standing || ''
  const isIncomplete = !props.task.dateCompleted

  if (daysUntilDue <= 3 && (budget === 0 || (logged / budget) * 100 < 50)) {
    return 'Due soon with low progress or no budget'
  }
  if (budget > 0 && logged > budget) {
    return 'Over budget'
  }
  if (['Started', 'Blocked'].includes(status) && daysSinceCreated >= 7 && isIncomplete) {
    return 'Stuck for over a week'
  }
  if (remaining === 0 && isIncomplete) {
    return 'No remaining time but incomplete'
  }
  return ''
})
</script>

<style scoped>
.task-progress__track {
  display: flex;
  width: 100%;
  height: 9px;
  overflow: hidden;
  background: #e5e7eb;
  border-radius: 999px;
}

.task-progress__logged,
.task-progress__remaining,
.task-progress__empty {
  height: 100%;
  min-width: 0;
}

.task-progress__logged {
  background: #83BD48;
}

.task-progress__remaining {
  background: #0DABCB;
}

.task-progress__empty {
  flex: 1;
  background: #d1d5db;
}

.task-detail-grid {
  display: grid;
  gap: 1.5rem;
}

@media (min-width: 1024px) {
  .task-detail-grid {
    grid-template-columns: minmax(220px, 1fr) minmax(0, 3fr);
  }
}

.task-detail-heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #001A70;
  font-size: 0.875rem;
  font-weight: 700;
}

.task-detail-heading::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #cbd5e1;
}

.task-detail-row {
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  gap: 0.75rem;
  color: #374151;
}

.task-detail-row dt {
  color: #4b5563;
}

.task-detail-row dd {
  min-width: 0;
  margin: 0;
  color: #001A70;
  font-weight: 500;
}
</style>
